import { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { GitCompare, FileSpreadsheet, Download } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsArrayBuffer } from '@/lib/utils';
import { cn } from '@/lib/cn';

interface SheetData {
  name: string;
  rows: unknown[][];
}

async function read(f: File): Promise<SheetData[]> {
  const buf = await readFileAsArrayBuffer(f);
  const wb = XLSX.read(buf, { type: 'array' });
  return wb.SheetNames.map((name) => ({
    name,
    rows: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1 }) as unknown[][],
  }));
}

interface DiffCell {
  type: 'same' | 'changed' | 'added' | 'removed';
  a?: unknown;
  b?: unknown;
}

function diffSheets(a: unknown[][], b: unknown[][]): { rows: DiffCell[][]; stats: { changed: number; added: number; removed: number; same: number } } {
  const maxRows = Math.max(a.length, b.length);
  const maxCols = Math.max(...a.map((r) => r.length), ...b.map((r) => r.length), 0);
  const rows: DiffCell[][] = [];
  const stats = { changed: 0, added: 0, removed: 0, same: 0 };
  for (let r = 0; r < maxRows; r++) {
    const row: DiffCell[] = [];
    for (let c = 0; c < maxCols; c++) {
      const av = a[r]?.[c];
      const bv = b[r]?.[c];
      const aDef = av !== undefined && av !== '';
      const bDef = bv !== undefined && bv !== '';
      let cell: DiffCell;
      if (!aDef && !bDef) cell = { type: 'same' };
      else if (!aDef) {
        cell = { type: 'added', b: bv };
        stats.added++;
      } else if (!bDef) {
        cell = { type: 'removed', a: av };
        stats.removed++;
      } else if (String(av) === String(bv)) {
        cell = { type: 'same', a: av, b: bv };
        stats.same++;
      } else {
        cell = { type: 'changed', a: av, b: bv };
        stats.changed++;
      }
      row.push(cell);
    }
    rows.push(row);
  }
  return { rows, stats };
}

export function SpreadsheetDiff() {
  const [a, setA] = useState<{ file: File; data: SheetData[] } | null>(null);
  const [b, setB] = useState<{ file: File; data: SheetData[] } | null>(null);
  const [sheetIdx, setSheetIdx] = useState(0);

  const diff = useMemo(() => {
    if (!a || !b) return null;
    const sA = a.data[sheetIdx]?.rows ?? [];
    const sB = b.data[sheetIdx]?.rows ?? [];
    return diffSheets(sA, sB);
  }, [a, b, sheetIdx]);

  async function handleA(files: File[]) {
    const f = files[0];
    if (!f) return;
    setA({ file: f, data: await read(f) });
  }
  async function handleB(files: File[]) {
    const f = files[0];
    if (!f) return;
    setB({ file: f, data: await read(f) });
  }

  function exportDiff() {
    if (!diff || !a || !b) return;
    const out: unknown[][] = [];
    out.push(['Row', 'Col', 'Status', 'Original', 'Modified']);
    diff.rows.forEach((row, r) => {
      row.forEach((cell, c) => {
        if (cell.type !== 'same') {
          out.push([r + 1, c + 1, cell.type, cell.a ?? '', cell.b ?? '']);
        }
      });
    });
    const ws = XLSX.utils.aoa_to_sheet(out);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'diff');
    const bytes = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([bytes], { type: 'application/octet-stream' }), 'diff.xlsx');
    toast.success('Diff exported');
  }

  const sheetNames = (a && b && a.data.length > 0 && b.data.length > 0)
    ? Array.from(new Set([...a.data.map((s) => s.name), ...b.data.map((s) => s.name)]))
    : [];

  return (
    <div>
      <ToolHeader
        toolId="spreadsheet-diff"
        title="Spreadsheet diff"
        description="Compare two CSV or Excel files cell by cell. Highlights additions, removals, and changes."
        badges={
          diff ? (
            <>
              <Badge variant="warning">{diff.stats.changed} changed</Badge>
              <Badge variant="success">+{diff.stats.added}</Badge>
              <Badge variant="danger">−{diff.stats.removed}</Badge>
            </>
          ) : null
        }
        actions={
          diff && (
            <Button size="sm" variant="ghost" onClick={exportDiff}>
              <Download size={11} />
              Export
            </Button>
          )
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card>
          <div className="text-xs font-medium text-fg-muted mb-2">Original</div>
          {!a ? (
            <Dropzone
              accept=".xlsx,.xls,.csv,.tsv"
              onFiles={handleA}
              icon={<FileSpreadsheet size={18} />}
              hint="CSV, XLSX, XLS, TSV"
            />
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium truncate flex items-center gap-2">
                <FileSpreadsheet size={14} className="text-fg-subtle" />
                {a.file.name}
              </div>
              <Button size="sm" variant="ghost" onClick={() => setA(null)}>
                Change
              </Button>
            </div>
          )}
        </Card>
        <Card>
          <div className="text-xs font-medium text-fg-muted mb-2">Modified</div>
          {!b ? (
            <Dropzone
              accept=".xlsx,.xls,.csv,.tsv"
              onFiles={handleB}
              icon={<FileSpreadsheet size={18} />}
              hint="CSV, XLSX, XLS, TSV"
            />
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium truncate flex items-center gap-2">
                <FileSpreadsheet size={14} className="text-fg-subtle" />
                {b.file.name}
              </div>
              <Button size="sm" variant="ghost" onClick={() => setB(null)}>
                Change
              </Button>
            </div>
          )}
        </Card>
      </div>

      {sheetNames.length > 1 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-xs text-fg-muted">Sheet:</span>
          {sheetNames.map((name, i) => (
            <button
              key={name}
              onClick={() => setSheetIdx(i)}
              className={cn(
                'px-2.5 py-1 text-xs rounded-md border transition',
                i === sheetIdx ? 'border-accent bg-accent/10 text-accent' : 'border-border',
              )}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      {!diff ? (
        <Card>
          <div className="py-12 text-center text-sm text-fg-subtle">
            <GitCompare size={28} className="mx-auto mb-2 text-fg-subtle/50" />
            Add both files to see the diff.
          </div>
        </Card>
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-auto max-h-[560px]">
            <table className="w-full text-sm">
              <tbody>
                {diff.rows.map((row, r) => (
                  <tr key={r}>
                    <td className="px-3 py-1.5 text-2xs font-mono text-fg-subtle border-b border-border w-12 bg-bg-inset sticky left-0">
                      {r + 1}
                    </td>
                    {row.map((cell, c) => (
                      <td
                        key={c}
                        className={cn(
                          'px-3 py-1.5 border-b border-border whitespace-nowrap font-mono text-xs',
                          cell.type === 'changed' && 'bg-warning/10 text-warning',
                          cell.type === 'added' && 'bg-success/10 text-success',
                          cell.type === 'removed' && 'bg-danger/10 text-danger',
                        )}
                        title={
                          cell.type === 'changed'
                            ? `${String(cell.a)} → ${String(cell.b)}`
                            : undefined
                        }
                      >
                        {cell.type === 'changed' ? (
                          <>
                            <span className="line-through opacity-60">{String(cell.a)}</span>
                            <span className="mx-1">→</span>
                            <span>{String(cell.b)}</span>
                          </>
                        ) : cell.type === 'added' ? (
                          <span>+ {String(cell.b)}</span>
                        ) : cell.type === 'removed' ? (
                          <span className="line-through">{String(cell.a)}</span>
                        ) : (
                          String(cell.a ?? '')
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export default SpreadsheetDiff;
